import { useAuthContext, useUploadFile } from '@core/shared';
import { AccountData, OptionsApi } from '@core/swagger-client';
import { Button, Delete, ErrorMessage, ImageAltText, ProfileAbbreviation, Upload, useFileDropzone } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

export const EditProfileImage = ({ account }: { account: AccountData }) => {
    const { t } = useTranslation();
    const { refreshToken } = useAuthContext();
    const { upload, progress, isSuccess } = useUploadFile(`/api/account/profile-image`);
    const { getRootProps, getInputProps, isDragActive, open, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            const filePayload = new FormData();
            filePayload.append('file', acceptedFiles[0]);
            upload(filePayload).then(() => {
                refreshToken();
            });
        },
        accept: {
            'image/png': ['.png'],
            'image/jpg': ['.jpg'],
            'image/webp': ['.webp']
        }
    });

    const altTextMutation = useMutation(OptionsApi.account.updateProfileImageAltText());
    const deleteProfileImageMutation = useMutation(OptionsApi.account.deleteProfileImage());
    const deleteImage = (e: MouseEvent) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        deleteProfileImageMutation.mutate(undefined, { onSuccess: () => refreshToken() });
    };

    if (!account) {
        return <></>;
    }

    const assets = account.assets;
    const profile = account.profile;
    return (
        <>
            <p className="text-sm mb-3 opacity-80">{t('Account.Images.Label')}</p>
            <div className="relative pt-4 menu:pt-0 z-10">
                <div className="relative flex items-start -mb-4 ps-4 md:ps-10">
                    <div
                        {...getRootProps()}
                        className={`group relative flex-none size-20 menu:size-24 md:size-30 ${isDragActive ? 'bg-accent-lighter' : 'bg-account '} hover:bg-accent-lighter focus-visible:bg-accent-lighter rounded-lg flex items-center justify-center text-white text-3xl md:text-5xl uppercase font-light outline outline-2 outline-white overflow-hidden transition-colors duration-150`}
                    >
                        {!assets.profileImage && <ProfileAbbreviation profile={profile} />}
                        {assets.profileImage && <img src={assets.profileImage.imageUrl} alt="" />}
                        <div
                            className={`absolute inset-0 rounded-lg border-2 border-dashed ${!assets.profileImage && !isDragActive && 'border-white'} ${assets.profileImage && !isDragActive && 'border-transparent'} ${isDragActive && 'border-accent'} group-hover:border-accent group-focus-visible:border-accent transition-colors duration-150`}
                        ></div>
                    </div>
                    <div className="flex flex-wrap gap-4 ms-4">
                        <Button variant="primary-outline" onClick={open} aria-label={t('Account.Images.Button.UploadProfileImage.AriaLabel')}>
                            <Upload classNames="size-6 flex-none stroke-current menu:me-2" />
                            <span className="hidden menu:inline">{t('Generic.Button.UploadImage')}</span>
                        </Button>
                        {assets.profileImage && (
                            <ImageAltText
                                altText={assets.profileImage.altText}
                                onSave={(altText) => altTextMutation.mutateAsync(altText).then(() => refreshToken())}
                            />
                        )}
                        {assets.profileImage && (
                            <Button
                                onClick={deleteImage}
                                variant="warning-outline"
                                aria-label={t('Account.Images.Button.DeleteProfileImage.AriaLabel')}
                                isLoading={deleteProfileImageMutation.isPending}
                            >
                                <Delete classNames="size-6 flex-none stroke-current menu:me-2" />
                                <span className="hidden menu:inline">{t('Generic.Button.Delete')}</span>
                            </Button>
                        )}
                    </div>
                    <input {...getInputProps()} />
                </div>
                {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
            </div>
        </>
    );
};
